<template>
  <div class="docs" :class="{ 'is-rtl': isRtl }">
    <!-- ambient decor, kept subtle for readability -->
    <div class="docs-decor">
      <div class="decor-orb orb-1"></div>
      <div class="decor-orb orb-2"></div>
      <div class="decor-orb orb-3"></div>
    </div>

    <!-- mobile section selector -->
    <div class="docs-mobilebar" v-if="serverConfig.view === 'mobile'">
      <select class="docs-mobilebar-select" :value="section" @change="goto($event.target.value)">
        <optgroup v-for="grp in nav" :key="grp.title" :label="t(grp.title)">
          <option v-for="item in grp.items" :key="item.id" :value="item.id">{{ t(item.label) }}</option>
        </optgroup>
      </select>
    </div>

    <div class="docs-shell">
      <!-- ===== Sidebar ===== -->
      <aside class="docs-sidebar" v-if="serverConfig.view !== 'mobile'">
        <div class="docs-sidebar-inner">
          <div class="docs-eyebrow">{{ t('DOCS_EYEBROW') }}</div>
          <nav v-for="grp in nav" :key="grp.title" class="docs-navgroup">
            <div class="docs-navgroup-title">{{ t(grp.title) }}</div>
            <a
              v-for="item in grp.items"
              :key="item.id"
              class="docs-navlink"
              :class="{ 'is-active': item.id === section }"
              @click="goto(item.id)"
            >{{ t(item.label) }}</a>
          </nav>
        </div>
      </aside>

      <!-- ===== Content ===== -->
      <main class="docs-content" @click="onDocClick">
        <div class="docs-article">
          <div class="docs-breadcrumb">
            <span>{{ t('DOCS_BREADCRUMB_ROOT') }}</span><span class="sep">/</span><span>{{ t(activeGroup) }}</span>
          </div>
          <h1 class="docs-h1">{{ t(activeItem.label) }}</h1>
          <p class="docs-subtitle" v-if="subtitleKey">{{ t(subtitleKey) }}</p>
          <div class="docs-titlerule"></div>

          <!-- ===================== QUICKSTART ===================== -->
          <template v-if="section === 'quickstart'">
            <p class="docs-lead" v-html="t('DOCS_QS_LEAD')"></p>

            <FeatureGrid accent :items="[
              { t: t('DOCS_QS_CON_QUEEN_T'), d: t('DOCS_QS_CON_QUEEN_D') },
              { t: t('DOCS_QS_CON_PAWN_T'), d: t('DOCS_QS_CON_PAWN_D') }
            ]" />

            <OsTabs :os="os" :linux="t('DOCS_OS_LINUX')" :windows="t('DOCS_OS_WINDOWS')" @set="setOs" />

            <Step n="1" :title="t('DOCS_QS_S1_TITLE')">
              <template v-if="os === 'linux'">
                <CodeBlock :code="'tar xzf enbarr-*.tar.gz\nsudo ./enbarr/install.sh'" v-bind="cp" />
                <p v-html="t('DOCS_QS_S1_LIN')"></p>
              </template>
              <template v-else>
                <p v-html="t('DOCS_QS_S1_WIN_PRE')"></p>
                <CodeBlock :code="'.\\Install-EnbarrQueen.ps1'" v-bind="cp" />
                <p v-html="t('DOCS_QS_S1_WIN_POST')"></p>
              </template>
            </Step>

            <Step n="2" :title="t('DOCS_QS_S2_TITLE')">
              <template v-if="os === 'linux'">
                <CodeBlock :code="'sudo cp /etc/enbarr/config.queen.json /etc/enbarr/config.json\nsudo systemctl restart enbarr-net'" v-bind="cp" />
              </template>
              <template v-else>
                <p v-html="t('DOCS_QS_S2_WIN')"></p>
              </template>
            </Step>

            <Step n="3" :title="t('DOCS_QS_S3_TITLE')">
              <p v-html="t('DOCS_QS_S3_PRE')"></p>
              <CodeBlock :code="'http://localhost:7779'" v-bind="cp" />
              <p>
                <span v-html="t('DOCS_QS_S3_POST')"></span>
                <span v-if="os === 'linux'" v-html="' ' + t('DOCS_QS_S3_LIN')"></span>
              </p>
            </Step>

            <Step n="4" :title="t('DOCS_QS_S4_TITLE')" :last="true">
              <Callout type="info" :label="t('DOCS_CL_NOTE')"><span v-html="t('DOCS_QS_FW')"></span></Callout>
              <p v-html="t('DOCS_QS_S4_PRE')"></p>
              <template v-if="os === 'linux'">
                <p v-html="t('DOCS_QS_S4_LIN')"></p>
              </template>
              <template v-else>
                <p v-html="t('DOCS_QS_S4_WIN_PRE')"></p>
                <CodeBlock :code="'msiexec /i enbarr.msi JOIN_CODE=&lt;token&gt; /qn'" v-bind="cp" />
              </template>
              <p v-html="t('DOCS_QS_S4_POST')"></p>
            </Step>

            <h2 class="docs-h2">{{ t('DOCS_QS_CONFIG_H') }}</h2>
            <p v-html="t('DOCS_QS_CONFIG_INTRO')"></p>
            <Pills :value="configView" :options="[
              { value: 'queen', label: t('DOCS_INST_CONFIG_QUEEN') },
              { value: 'pawn', label: t('DOCS_INST_CONFIG_PAWN') }
            ]" @set="setConfigView" />
            <CodeBlock :code="configView === 'queen' ? queenConfigAnn : pawnConfigAnn" v-bind="cp" />
            <p class="docs-note" v-html="t('DOCS_QS_CONFIG_NOTE')"></p>

            <Callout type="next" :label="t('DOCS_CL_NEXT')"><span v-html="t('DOCS_QS_NEXT')"></span></Callout>
          </template>

          <!-- ===================== OVERVIEW (incl. how it works) ===================== -->
          <template v-else-if="section === 'overview'">
            <p class="docs-lead" v-html="t('DOCS_OV_LEAD')"></p>

            <h2 class="docs-h2">{{ t('DOCS_OV_H_QUEEN') }}</h2>
            <p v-html="t('DOCS_OV_QUEEN_P')"></p>

            <FeatureGrid accent :items="[
              { t: t('DOCS_OV_F1_T'), d: t('DOCS_OV_F1_D') },
              { t: t('DOCS_OV_F2_T'), d: t('DOCS_OV_F2_D') },
              { t: t('DOCS_OV_F3_T'), d: t('DOCS_OV_F3_D') },
              { t: t('DOCS_OV_F4_T'), d: t('DOCS_OV_F4_D') }
            ]" />

            <h2 class="docs-h2">{{ t('DOCS_HIW_H_DETECT') }}</h2>
            <p v-html="t('DOCS_HIW_DETECT_P')"></p>

            <h2 class="docs-h2">{{ t('DOCS_HIW_H_COMM') }}</h2>
            <ul class="docs-list">
              <li v-html="t('DOCS_HIW_COMM_1')"></li>
              <li v-html="t('DOCS_HIW_COMM_2')"></li>
              <li v-html="t('DOCS_HIW_COMM_3')"></li>
            </ul>

            <h2 class="docs-h2">{{ t('DOCS_OV_H_ALERT') }}</h2>
            <p v-html="t('DOCS_OV_ALERT_P')"></p>
            <ol class="docs-steps-ol">
              <li v-html="t('DOCS_HIW_LIFE_1')"></li>
              <li v-html="t('DOCS_HIW_LIFE_2')"></li>
              <li v-html="t('DOCS_HIW_LIFE_3')"></li>
              <li v-html="t('DOCS_HIW_LIFE_4')"></li>
            </ol>

            <h2 class="docs-h2">{{ t('DOCS_HIW_H_RESILIENCE') }}</h2>
            <p v-html="t('DOCS_HIW_RESILIENCE_P')"></p>

            <h2 class="docs-h2">{{ t('DOCS_OV_H_SELFHOSTED') }}</h2>
            <p v-html="t('DOCS_OV_SELFHOSTED_P')"></p>

            <Callout type="next" :label="t('DOCS_CL_NEXT')"><span v-html="t('DOCS_OV_NEXT')"></span></Callout>
          </template>

          <!-- ===================== ARCHITECTURE ===================== -->
          <template v-else-if="section === 'architecture'">
            <p class="docs-lead" v-html="t('DOCS_ARCH_LEAD')"></p>

            <h2 class="docs-h2">{{ t('DOCS_ARCH_H_ROLES') }}</h2>
            <p v-html="t('DOCS_ARCH_ROLES_P')"></p>
            <FeatureGrid accent :items="[
              { t: t('DOCS_ARCH_R_QUEEN_T'), d: t('DOCS_ARCH_R_QUEEN_D') },
              { t: t('DOCS_ARCH_R_PAWN_T'), d: t('DOCS_ARCH_R_PAWN_D') },
              { t: t('DOCS_ARCH_R_KNIGHT_T'), d: t('DOCS_ARCH_R_KNIGHT_D') }
            ]" />

            <h2 class="docs-h2">{{ t('DOCS_ARCH_H_PROC') }}</h2>
            <p v-html="t('DOCS_ARCH_PROC_P')"></p>
            <FeatureGrid accent :items="[
              { t: t('DOCS_ARCH_P1_T'), d: t('DOCS_ARCH_P1_D') },
              { t: t('DOCS_ARCH_P2_T'), d: t('DOCS_ARCH_P2_D') }
            ]" />

            <h2 class="docs-h2">{{ t('DOCS_ARCH_H_MESH') }}</h2>
            <p v-html="t('DOCS_ARCH_MESH_P')"></p>
            <ul class="docs-list">
              <li v-html="t('DOCS_ARCH_MESH_1')"></li>
              <li v-html="t('DOCS_ARCH_MESH_2')"></li>
              <li v-html="t('DOCS_ARCH_MESH_3')"></li>
            </ul>

            <h2 class="docs-h2">{{ t('DOCS_ARCH_H_FLOW') }}</h2>
            <ol class="docs-steps-ol">
              <li v-html="t('DOCS_ARCH_FLOW_1')"></li>
              <li v-html="t('DOCS_ARCH_FLOW_2')"></li>
              <li v-html="t('DOCS_ARCH_FLOW_3')"></li>
              <li v-html="t('DOCS_ARCH_FLOW_4')"></li>
              <li v-html="t('DOCS_ARCH_FLOW_5')"></li>
            </ol>

            <h2 class="docs-h2">{{ t('DOCS_ARCH_H_PRINCIPLES') }}</h2>
            <FeatureGrid accent :items="[
              { t: t('DOCS_ARCH_PR1_T'), d: t('DOCS_ARCH_PR1_D') },
              { t: t('DOCS_ARCH_PR2_T'), d: t('DOCS_ARCH_PR2_D') },
              { t: t('DOCS_ARCH_PR3_T'), d: t('DOCS_ARCH_PR3_D') },
              { t: t('DOCS_ARCH_PR4_T'), d: t('DOCS_ARCH_PR4_D') },
              { t: t('DOCS_ARCH_PR5_T'), d: t('DOCS_ARCH_PR5_D') },
              { t: t('DOCS_ARCH_PR6_T'), d: t('DOCS_ARCH_PR6_D') }
            ]" />

            <h2 class="docs-h2">{{ t('DOCS_ARCH_H_GLOSSARY') }}</h2>
            <Table :head="[t('DOCS_ARCH_GL_T_TERM'), t('DOCS_ARCH_GL_T_DEF')]" :rows="[
              ['Queen', t('DOCS_ARCH_GL_QUEEN')],
              ['Pawn', t('DOCS_ARCH_GL_PAWN')],
              ['Knight', t('DOCS_ARCH_GL_KNIGHT')],
              ['Swarm key', t('DOCS_ARCH_GL_SWARM')],
              ['Digest', t('DOCS_ARCH_GL_DIGEST')],
              ['Insight', t('DOCS_ARCH_GL_INSIGHT')],
              ['Investigation', t('DOCS_ARCH_GL_INVESTIGATION')]
            ]" />

            <Callout type="next" :label="t('DOCS_CL_NEXT')"><span v-html="t('DOCS_ARCH_NEXT')"></span></Callout>
          </template>

          <!-- ===================== INSTALL ===================== -->
          <template v-else-if="section === 'install'">
            <p class="docs-lead" v-html="t('DOCS_INST_LEAD')"></p>

            <OsTabs :os="os" :linux="t('DOCS_OS_LINUX')" :windows="t('DOCS_OS_WINDOWS')" @set="setOs" />

            <!-- ---------- LINUX ---------- -->
            <template v-if="os === 'linux'">
              <h2 class="docs-h2">{{ t('DOCS_INST_LIN_H_REQ') }}</h2>
              <ul class="docs-list">
                <li v-html="t('DOCS_INST_LIN_REQ_1')"></li>
                <li v-html="t('DOCS_INST_LIN_REQ_2')"></li>
                <li v-html="t('DOCS_INST_LIN_REQ_3')"></li>
              </ul>

              <h2 class="docs-h2">{{ t('DOCS_INST_LIN_H_INSTALL') }}</h2>
              <p v-html="t('DOCS_INST_LIN_INSTALL_P')"></p>

              <h3 class="docs-h3">{{ t('DOCS_INST_LIN_H_TARBALL') }}</h3>
              <CodeBlock :code="'tar xzf enbarr-*.tar.gz\nsudo ./enbarr/install.sh'" v-bind="cp" />
              <h3 class="docs-h3">{{ t('DOCS_INST_LIN_H_DEB') }}</h3>
              <CodeBlock :code="'sudo apt install ./enbarr_*.deb'" v-bind="cp" />
              <h3 class="docs-h3">{{ t('DOCS_INST_LIN_H_RPM') }}</h3>
              <CodeBlock :code="'sudo dnf install ./enbarr-*.rpm'" v-bind="cp" />

              <h2 class="docs-h2">{{ t('DOCS_INST_LIN_H_WHAT') }}</h2>
              <Table :head="['', t('DOCS_INST_LIN_T_LOCATION')]" :rows="[
                [t('DOCS_INST_LIN_T_BINARIES'), '/opt/enbarr/bin/enbarr-service, /opt/enbarr/bin/enbarr-net'],
                [t('DOCS_INST_LIN_T_CONFIG'), '/etc/enbarr/config.json, /etc/enbarr/config.queen.json'],
                [t('DOCS_INST_LIN_T_DATA'), '/var/lib/enbarr/'],
                [t('DOCS_INST_LIN_T_IPC'), '/run/enbarr/']
              ]" />

              <h2 class="docs-h2">{{ t('DOCS_INST_LIN_H_SERVICES') }}</h2>
              <Table :head="[t('DOCS_INST_LIN_T_SERVICE'), t('DOCS_INST_LIN_T_ROLE')]" :rows="[
                ['enbarr-service', t('DOCS_INST_LIN_SVC1')],
                ['enbarr-net', t('DOCS_INST_LIN_SVC2')]
              ]" />
              <p v-html="t('DOCS_INST_LIN_MANAGE')"></p>
              <CodeBlock :code="'sudo systemctl start   enbarr-service enbarr-net\nsudo systemctl enable  enbarr-service enbarr-net\nsudo systemctl status  enbarr-service enbarr-net\nsudo journalctl -u enbarr-net -f'" v-bind="cp" />

              <h2 class="docs-h2">{{ t('DOCS_INST_LIN_H_QUEEN') }}</h2>
              <p v-html="t('DOCS_INST_LIN_QUEEN_P')"></p>
              <CodeBlock :code="'sudo cp /etc/enbarr/config.queen.json /etc/enbarr/config.json\nsudo systemctl restart enbarr-net'" v-bind="cp" />

              <h2 class="docs-h2">{{ t('DOCS_INST_LIN_H_PORTS') }}</h2>
              <Table :head="[t('DOCS_INST_PORT'), t('DOCS_INST_PURPOSE')]" :rows="[
                ['7777', t('DOCS_INST_PORT_7777')],
                ['7778', t('DOCS_INST_PORT_7778')],
                ['7779', t('DOCS_INST_PORT_7779')]
              ]" />
              <p class="docs-note" v-html="t('DOCS_INST_LIN_PORTS_NOTE')"></p>

              <h2 class="docs-h2">{{ t('DOCS_INST_LIN_H_FIREWALL') }}</h2>
              <p v-html="t('DOCS_INST_LIN_FW_P')"></p>
              <h3 class="docs-h3">{{ t('DOCS_INST_LIN_FW_UFW') }}</h3>
              <CodeBlock :code="'sudo ufw allow 7777/tcp\nsudo ufw allow 7778/tcp'" v-bind="cp" />
              <h3 class="docs-h3">{{ t('DOCS_INST_LIN_FW_FIREWALLD') }}</h3>
              <CodeBlock :code="'sudo firewall-cmd --permanent --add-port=7777/tcp --add-port=7778/tcp\nsudo firewall-cmd --reload'" v-bind="cp" />

              <h2 class="docs-h2">{{ t('DOCS_INST_LIN_H_UNINSTALL') }}</h2>
              <p v-html="t('DOCS_INST_LIN_UNINSTALL_1')"></p>
              <CodeBlock :code="'sudo apt remove enbarr      # Debian/Ubuntu\nsudo dnf remove enbarr      # RHEL/Fedora'" v-bind="cp" />
              <p v-html="t('DOCS_INST_LIN_UNINSTALL_2')"></p>
              <CodeBlock :code="'sudo systemctl disable --now enbarr-service enbarr-net\nsudo rm -rf /opt/enbarr /etc/enbarr /var/lib/enbarr /run/enbarr\nsudo systemctl daemon-reload'" v-bind="cp" />
            </template>

            <!-- ---------- WINDOWS ---------- -->
            <template v-else>
              <Callout type="warn" :label="t('DOCS_CL_HEADSUP')"><span v-html="t('DOCS_INST_WIN_WARN')"></span></Callout>

              <h2 class="docs-h2">{{ t('DOCS_INST_WIN_H_REQ') }}</h2>
              <ul class="docs-list">
                <li v-html="t('DOCS_INST_WIN_REQ_1')"></li>
                <li v-html="t('DOCS_INST_WIN_REQ_2')"></li>
              </ul>

              <h2 class="docs-h2">{{ t('DOCS_INST_WIN_H_MSI') }}</h2>
              <p v-html="t('DOCS_INST_WIN_MSI_P1')"></p>
              <CodeBlock :code="'msiexec /i enbarr.msi /quiet'" v-bind="cp" />
              <p v-html="t('DOCS_INST_WIN_MSI_P2')"></p>
              <CodeBlock :code="'msiexec /i enbarr.msi JOIN_CODE=&lt;token&gt; /qn'" v-bind="cp" />

              <h2 class="docs-h2">{{ t('DOCS_INST_WIN_H_QUEEN') }}</h2>
              <p v-html="t('DOCS_INST_WIN_QUEEN_P1')"></p>
              <CodeBlock :code="'.\\Install-EnbarrQueen.ps1 -WebPort 7779 -GatewayPort 7778'" v-bind="cp" />
              <p v-html="t('DOCS_INST_WIN_QUEEN_P2')"></p>
              <Table :head="[t('DOCS_INST_WIN_T_PARAM'), t('DOCS_INST_WIN_T_MEANING')]" :rows="[
                ['-InferenceEndpoint', t('DOCS_INST_WIN_P_INFEP')],
                ['-InferenceModel', t('DOCS_INST_WIN_P_INFMODEL')],
                ['-WebPort', t('DOCS_INST_WIN_P_WEBPORT')],
                ['-GatewayPort', t('DOCS_INST_WIN_P_GWPORT')],
                ['-SwarmKeyPath', t('DOCS_INST_WIN_P_SWARM')],
                ['-Force', t('DOCS_INST_WIN_P_FORCE')]
              ]" />

              <h2 class="docs-h2">{{ t('DOCS_INST_WIN_H_SERVICES') }}</h2>
              <Table :head="[t('DOCS_INST_WIN_T_SERVICE'), t('DOCS_INST_WIN_T_ROLE')]" :rows="[
                ['EnbarrService', t('DOCS_INST_WIN_SVC1')],
                ['EnbarrNet', t('DOCS_INST_WIN_SVC2')]
              ]" />
              <p v-html="t('DOCS_INST_WIN_MANAGE')"></p>
              <CodeBlock :code="'Get-Service EnbarrService, EnbarrNet\nStart-Service EnbarrNet\nStop-Service  EnbarrNet'" v-bind="cp" />
              <p v-html="t('DOCS_INST_WIN_SELFINSTALL')"></p>
              <CodeBlock :code="'enbarr-net.exe --install --data-dir &lt;dir&gt; --role queen\nenbarr-net.exe --uninstall'" v-bind="cp" />

              <h2 class="docs-h2">{{ t('DOCS_INST_WIN_H_FW') }}</h2>
              <p v-html="t('DOCS_INST_WIN_FW_P')"></p>
              <p v-html="t('DOCS_INST_WIN_FW_PORTS')"></p>
              <CodeBlock :code="winFwCmd" v-bind="cp" />

              <h2 class="docs-h2">{{ t('DOCS_INST_WIN_H_UNINSTALL') }}</h2>
              <CodeBlock :code="'msiexec /x enbarr.msi /quiet'" v-bind="cp" />
              <p v-html="t('DOCS_INST_WIN_UNINSTALL_P')"></p>
              <CodeBlock :code="'enbarr-net.exe --uninstall\nEnbarrService.exe --uninstall'" v-bind="cp" />
            </template>

            <!-- ---------- Configuration (shared) ---------- -->
            <h2 class="docs-h2">{{ t('DOCS_INST_H_CONFIG') }}</h2>
            <p v-html="t('DOCS_INST_CONFIG_P')"></p>
            <Pills :value="configView" :options="[
              { value: 'queen', label: t('DOCS_INST_CONFIG_QUEEN') },
              { value: 'pawn', label: t('DOCS_INST_CONFIG_PAWN') }
            ]" @set="setConfigView" />
            <CodeBlock :code="configView === 'queen' ? queenConfig : pawnConfig" v-bind="cp" />

            <h3 class="docs-h3">{{ t('DOCS_INST_CF_H') }}</h3>
            <Table :head="[t('DOCS_INST_CF_T_FIELD'), t('DOCS_INST_CF_T_DESC')]" :rows="[
              ['role', t('DOCS_INST_CF_ROLE')],
              ['dataDir', t('DOCS_INST_CF_DATADIR')],
              ['web.enabled', t('DOCS_INST_CF_WEBEN')],
              ['web.addr', t('DOCS_INST_CF_WEBADDR')],
              ['gateway.addr', t('DOCS_INST_CF_GWADDR')],
              ['gateway.deployKey', t('DOCS_INST_CF_DEPLOYKEY')],
              ['network.port', t('DOCS_INST_CF_PORT')],
              ['network.swarmKeyPath', t('DOCS_INST_CF_SWARM')],
              ['network.joinCode', t('DOCS_INST_CF_JOINCODE')],
              ['agent.clearance', t('DOCS_INST_CF_CLEARANCE')],
              ['platform.alertMode', t('DOCS_INST_CF_ALERTMODE')]
            ]" />

            <Callout type="next" :label="t('DOCS_CL_NEXT')"><span v-html="t('DOCS_INST_NEXT')"></span></Callout>
          </template>

          <!-- ===================== INFERENCE ===================== -->
          <template v-else-if="section === 'inference'">
            <p class="docs-lead" v-html="t('DOCS_INF_LEAD')"></p>

            <h2 class="docs-h2">{{ t('DOCS_INF_H_BYO') }}</h2>
            <p v-html="t('DOCS_INF_BYO_P')"></p>

            <h2 class="docs-h2">{{ t('DOCS_INF_H_WHERE') }}</h2>
            <p v-html="t('DOCS_INF_WHERE_P')"></p>
            <CodeBlock :code="inferenceConfig" v-bind="cp" />

            <h2 class="docs-h2">{{ t('DOCS_INF_H_FIELDS') }}</h2>
            <Table :head="[t('DOCS_INF_CF_T_FIELD'), t('DOCS_INF_CF_T_DESC')]" :rows="[
              ['inference.mode', t('DOCS_INF_F_MODE')],
              ['inference.endpoint', t('DOCS_INF_F_ENDPOINT')],
              ['inference.model', t('DOCS_INF_F_MODEL')],
              ['inference.apiKey', t('DOCS_INF_F_APIKEY')],
              ['agent.llmEndpoint', t('DOCS_INF_F_AGENTEP')]
            ]" />

            <h2 class="docs-h2">{{ t('DOCS_INF_H_PROVIDERS') }}</h2>
            <p v-html="t('DOCS_INF_PROV_P')"></p>

            <Callout type="tip" :label="t('DOCS_CL_TIP')"><span v-html="t('DOCS_INF_NOTE')"></span></Callout>
            <Callout type="next" :label="t('DOCS_CL_NEXT')"><span v-html="t('DOCS_INF_NEXT')"></span></Callout>
          </template>

          <!-- ===================== ENROL ===================== -->
          <template v-else-if="section === 'enrol'">
            <p class="docs-lead" v-html="t('DOCS_ENR_LEAD')"></p>

            <Callout type="info" :label="t('DOCS_CL_NOTE')"><span v-html="t('DOCS_ENR_PREREQ')"></span></Callout>

            <h2 class="docs-h2">{{ t('DOCS_ENR_H_A') }}</h2>
            <OsTabs :os="os" :linux="t('DOCS_OS_LINUX')" :windows="t('DOCS_OS_WINDOWS')" @set="setOs" />
            <ol class="docs-steps-ol">
              <li v-html="t('DOCS_ENR_A_1')"></li>
              <li v-html="t('DOCS_ENR_A_2')"></li>
              <li v-html="os === 'linux' ? t('DOCS_ENR_A_3_LIN') : t('DOCS_ENR_A_3_WIN')"></li>
              <li v-html="t('DOCS_ENR_A_4')"></li>
            </ol>

            <h2 class="docs-h2">{{ t('DOCS_ENR_H_B') }}</h2>
            <p v-html="t('DOCS_ENR_B_P')"></p>

            <Callout type="info" :label="t('DOCS_CL_NOTE')"><span v-html="t('DOCS_ENR_GATES')"></span></Callout>
          </template>

          <!-- ===================== ROLLOUT ===================== -->
          <template v-else-if="section === 'rollout'">
            <p class="docs-lead" v-html="t('DOCS_ROLL_LEAD')"></p>

            <h2 class="docs-h2">{{ t('DOCS_ROLL_H_SCRIPT') }}</h2>
            <p v-html="t('DOCS_ROLL_SCRIPT_P')"></p>
            <Table :head="[t('DOCS_ROLL_T_PARAM'), t('DOCS_ROLL_T_MEANING')]" :rows="[
              ['-QueenUrl', t('DOCS_ROLL_P_QUEENURL')],
              ['-DeployKey', t('DOCS_ROLL_P_DEPLOYKEY')],
              ['-SwarmKeyPath', t('DOCS_ROLL_P_SWARM')],
              ['-InstallDir / -DataDir', t('DOCS_ROLL_P_DIRS')],
              ['-RemoteActions', t('DOCS_ROLL_P_REMOTE')],
              ['-Force', t('DOCS_ROLL_P_FORCE')]
            ]" />

            <h3 class="docs-h3">{{ t('DOCS_ROLL_H_EXAMPLE') }}</h3>
            <CodeBlock :code="rolloutExample" v-bind="cp" />

            <h2 class="docs-h2">{{ t('DOCS_ROLL_H_PERMACHINE') }}</h2>
            <ol class="docs-steps-ol">
              <li v-html="t('DOCS_ROLL_PM_1')"></li>
              <li v-html="t('DOCS_ROLL_PM_2')"></li>
              <li v-html="t('DOCS_ROLL_PM_3')"></li>
              <li v-html="t('DOCS_ROLL_PM_4')"></li>
              <li v-html="t('DOCS_ROLL_PM_5')"></li>
            </ol>

            <h2 class="docs-h2">{{ t('DOCS_ROLL_H_SCALE') }}</h2>
            <p v-html="t('DOCS_ROLL_SCALE_P')"></p>
            <FeatureGrid accent :items="[
              { t: t('DOCS_ROLL_S1_T'), d: t('DOCS_ROLL_S1_D') },
              { t: t('DOCS_ROLL_S2_T'), d: t('DOCS_ROLL_S2_D') },
              { t: t('DOCS_ROLL_S3_T'), d: t('DOCS_ROLL_S3_D') }
            ]" />
            <Callout type="warn" :label="t('DOCS_CL_HEADSUP')"><span v-html="t('DOCS_ROLL_RATE')"></span></Callout>
          </template>

          <!-- ===================== LICENSING ===================== -->
          <template v-else-if="section === 'licensing'">
            <p class="docs-lead" v-html="t('DOCS_LIC_LEAD')"></p>

            <h2 class="docs-h2">{{ t('DOCS_LIC_H_TIERS') }}</h2>
            <FeatureGrid accent :items="[
              { t: t('DOCS_LIC_T1_T'), d: t('DOCS_LIC_T1_D') },
              { t: t('DOCS_LIC_T2_T'), d: t('DOCS_LIC_T2_D') }
            ]" />

            <h2 class="docs-h2">{{ t('DOCS_LIC_H_INSTALL') }}</h2>
            <ol class="docs-steps-ol">
              <li v-html="t('DOCS_LIC_I_1')"></li>
              <li v-html="t('DOCS_LIC_I_2')"></li>
              <li v-html="t('DOCS_LIC_I_3')"></li>
            </ol>

            <h2 class="docs-h2">{{ t('DOCS_LIC_H_STATES') }}</h2>
            <Table :head="[t('DOCS_LIC_T_STATE'), t('DOCS_LIC_T_MEANS')]" :rows="[
              ['active', t('DOCS_LIC_ST_ACTIVE')],
              ['grace', t('DOCS_LIC_ST_GRACE')],
              ['degraded', t('DOCS_LIC_ST_DEGRADED')],
              ['stopped', t('DOCS_LIC_ST_STOPPED')],
              ['unlicensed', t('DOCS_LIC_ST_UNLICENSED')]
            ]" />
            <Callout type="info" :label="t('DOCS_CL_NOTE')"><span v-html="t('DOCS_LIC_NOTE')"></span></Callout>
          </template>

          <!-- ===================== DASHBOARD ===================== -->
          <template v-else-if="section === 'dashboard'">
            <p class="docs-lead" v-html="t('DOCS_DASH_LEAD')"></p>

            <h2 class="docs-h2">{{ t('DOCS_DASH_H_LAYOUT') }}</h2>
            <p v-html="t('DOCS_DASH_LAYOUT_P')"></p>

            <h2 class="docs-h2">{{ t('DOCS_DASH_H_FLEET') }}</h2>
            <Table :head="[t('DOCS_DASH_T_TAB'), t('DOCS_DASH_T_SHOWS')]" :rows="[
              ['Events', t('DOCS_DASH_FLEET_EVENTS')],
              ['Peers', t('DOCS_DASH_FLEET_PEERS')],
              ['Incidents', t('DOCS_DASH_FLEET_INCIDENTS')],
              ['Enrolments', t('DOCS_DASH_FLEET_ENROL')]
            ]" />

            <h2 class="docs-h2">{{ t('DOCS_DASH_H_SETTINGS') }}</h2>
            <Table :head="[t('DOCS_DASH_T_TAB'), t('DOCS_DASH_T_CONTROLS')]" :rows="[
              ['Inference', t('DOCS_DASH_SET_INFERENCE')],
              ['Platform', t('DOCS_DASH_SET_PLATFORM')],
              ['License', t('DOCS_DASH_SET_LICENSE')],
              ['Tools', t('DOCS_DASH_SET_TOOLS')],
              ['Concurrency', t('DOCS_DASH_SET_CONCURRENCY')]
            ]" />

            <h2 class="docs-h2">{{ t('DOCS_DASH_H_ALERT') }}</h2>
            <p v-html="t('DOCS_DASH_ALERT_P')"></p>
          </template>

          <!-- ===================== REFERENCE ===================== -->
          <template v-else-if="section === 'reference'">
            <p class="docs-lead" v-html="t('DOCS_REF_LEAD')"></p>

            <h2 class="docs-h2">{{ t('DOCS_REF_H_PORTS') }}</h2>
            <Table :head="[t('DOCS_INST_PORT'), t('DOCS_INST_PURPOSE')]" :rows="[
              ['7777', t('DOCS_REF_PORT_7777')],
              ['7778', t('DOCS_REF_PORT_7778')],
              ['7779', t('DOCS_REF_PORT_7779')]
            ]" />

            <h2 class="docs-h2">{{ t('DOCS_REF_H_FIREWALL') }}</h2>
            <p v-html="t('DOCS_REF_FW_P')"></p>
            <CodeBlock :code="'sudo ufw allow 7777/tcp\nsudo ufw allow 7778/tcp'" v-bind="cp" />

            <h2 class="docs-h2">{{ t('DOCS_REF_H_FILES') }}</h2>
            <Table :head="['', t('DOCS_REF_T_PATH')]" :rows="[
              [t('DOCS_REF_F_BINARIES'), '/opt/enbarr/bin/'],
              [t('DOCS_REF_F_CONFIG'), '/etc/enbarr/'],
              [t('DOCS_REF_F_DATA'), '/var/lib/enbarr/'],
              [t('DOCS_REF_F_IPC'), '/run/enbarr/'],
              [t('DOCS_REF_F_LOGS'), 'journalctl -u enbarr-net / -u enbarr-service']
            ]" />

            <h2 class="docs-h2">{{ t('DOCS_REF_H_CONFIG') }}</h2>
            <p v-html="t('DOCS_REF_CONFIG_P')"></p>
            <Table :head="[t('DOCS_INST_CF_T_FIELD'), t('DOCS_INST_CF_T_DESC')]" :rows="[
              ['role', t('DOCS_INST_CF_ROLE')],
              ['web.addr', t('DOCS_INST_CF_WEBADDR')],
              ['gateway.addr', t('DOCS_INST_CF_GWADDR')],
              ['network.swarmKeyPath', t('DOCS_INST_CF_SWARM')],
              ['network.joinCode', t('DOCS_INST_CF_JOINCODE')],
              ['platform.alertMode', t('DOCS_INST_CF_ALERTMODE')]
            ]" />

            <h2 class="docs-h2">{{ t('DOCS_REF_H_TROUBLE') }}</h2>
            <Table :head="[t('DOCS_REF_T_SYMPTOM'), t('DOCS_REF_T_CAUSE')]" :rows="[
              [t('DOCS_REF_TR1_S'), t('DOCS_REF_TR1_C')],
              [t('DOCS_REF_TR2_S'), t('DOCS_REF_TR2_C')],
              [t('DOCS_REF_TR3_S'), t('DOCS_REF_TR3_C')],
              [t('DOCS_REF_TR4_S'), t('DOCS_REF_TR4_C')]
            ]" />

            <Callout type="info" :label="t('DOCS_CL_NOTE')"><span v-html="t('DOCS_REF_NOTE')"></span></Callout>
          </template>
        </div>

        <!-- prev / next -->
        <div class="docs-pager" v-if="prevItem || nextItem">
          <a v-if="prevItem" class="docs-pager-link prev" @click="goto(prevItem.id)">
            <span class="docs-pager-dir">&larr; {{ t('DOCS_PREV') }}</span>
            <span class="docs-pager-label">{{ t(prevItem.label) }}</span>
          </a>
          <span v-else></span>
          <a v-if="nextItem" class="docs-pager-link next" @click="goto(nextItem.id)">
            <span class="docs-pager-dir">{{ t('DOCS_NEXT') }} &rarr;</span>
            <span class="docs-pager-label">{{ t(nextItem.label) }}</span>
          </a>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import { computed, h, inject, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { translationStore, RTL_LANGS } from "@/stores/translation";

/* ---- small presentational helpers (render functions keep the template clean) ---- */

const Step = {
  name: "Step",
  props: { n: String, title: String, last: Boolean },
  setup(props, { slots }) {
    return () =>
      h("div", { class: ["docs-step", { "is-last": props.last }] }, [
        h("div", { class: "docs-step-rail" }, [h("div", { class: "docs-step-num" }, props.n)]),
        h("div", { class: "docs-step-body" }, [
          h("div", { class: "docs-step-title" }, props.title),
          h("div", { class: "docs-step-content" }, slots.default ? slots.default() : [])
        ])
      ]);
  }
};

const CodeBlock = {
  name: "CodeBlock",
  props: { code: String, copy: { type: String, default: "Copy" }, copied: { type: String, default: "Copied" } },
  setup(props) {
    const done = ref(false);
    const doCopy = () => {
      const text = props.code
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&amp;/g, "&");
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(() => {
          done.value = true;
          setTimeout(() => (done.value = false), 1400);
        });
      }
    };
    return () =>
      h("div", { class: "docs-code" }, [
        h("button", { class: "docs-code-copy", onClick: doCopy }, done.value ? props.copied : props.copy),
        h("pre", {}, [h("code", { innerHTML: props.code })])
      ]);
  }
};

const Callout = {
  name: "Callout",
  props: { type: { type: String, default: "info" }, label: { type: String, default: "Note" } },
  setup(props, { slots }) {
    return () =>
      h("div", { class: ["docs-callout", "is-" + props.type] }, [
        h("div", { class: "docs-callout-label" }, props.label),
        h("div", { class: "docs-callout-body" }, slots.default ? slots.default() : [])
      ]);
  }
};

const OsTabs = {
  name: "OsTabs",
  props: { os: String, linux: { type: String, default: "Linux" }, windows: { type: String, default: "Windows" } },
  emits: ["set"],
  setup(props, { emit }) {
    return () =>
      h("div", { class: "docs-ostabs" }, [
        h("div", { class: "docs-ostabs-track" }, [
          h("button", { class: ["docs-ostab", { "is-active": props.os === "linux" }], onClick: () => emit("set", "linux") }, props.linux),
          h("button", { class: ["docs-ostab", { "is-active": props.os === "windows" }], onClick: () => emit("set", "windows") }, props.windows)
        ])
      ]);
  }
};

const Pills = {
  name: "Pills",
  props: { value: String, options: Array },
  emits: ["set"],
  setup(props, { emit }) {
    return () =>
      h("div", { class: "docs-ostabs" }, [
        h(
          "div",
          { class: "docs-ostabs-track" },
          props.options.map((o) =>
            h("button", { class: ["docs-ostab", { "is-active": props.value === o.value }], onClick: () => emit("set", o.value) }, o.label)
          )
        )
      ]);
  }
};

const FeatureGrid = {
  name: "FeatureGrid",
  props: { items: Array, accent: Boolean },
  setup(props) {
    return () =>
      h(
        "div",
        { class: ["docs-featuregrid", { "is-accent": props.accent }] },
        props.items.map((it) =>
          h("div", { class: "docs-feature" }, [
            h("div", { class: "docs-feature-t" }, it.t),
            h("div", { class: "docs-feature-d", innerHTML: it.d })
          ])
        )
      );
  }
};

const Table = {
  name: "Table",
  props: { rows: Array, head: Array },
  setup(props) {
    return () =>
      h("div", { class: "docs-tablewrap" }, [
        h("table", { class: "docs-table" }, [
          props.head ? h("thead", {}, [h("tr", {}, props.head.map((c) => h("th", { innerHTML: c })))]) : null,
          h("tbody", {}, props.rows.map((r) => h("tr", {}, r.map((c) => h("td", { innerHTML: c })))))
        ])
      ]);
  }
};

/* ---- literal (untranslated) code samples ----
   Clean versions mirror enbarr/etc/config.queen.json and enbarr/etc/config.json. */
const queenConfig = `{
  "role": "queen",
  "dataDir": "/var/lib/enbarr",
  "web": {
    "enabled": true,
    "addr": "127.0.0.1:7779"
  },
  "gateway": {
    "addr": "0.0.0.0:7778",
    "deployKey": ""
  },
  "network": {
    "port": 7777,
    "swarmKeyPath": "/var/lib/enbarr/swarm.key"
  },
  "agent": {
    "clearance": 200
  },
  "platform": {
    "alertMode": "automatic"
  }
}`;
const pawnConfig = `{
  "role": "pawn",
  "dataDir": "/var/lib/enbarr",
  "web": {
    "enabled": false,
    "addr": "127.0.0.1:7779"
  },
  "network": {
    "port": 7777,
    "swarmKeyPath": "/var/lib/enbarr/swarm.key",
    "joinCode": ""
  },
  "agent": {
    "clearance": 200
  }
}`;
/* annotated versions for the quickstart (// notes are explanatory, not valid JSON) */
const queenConfigAnn = `{
  "role": "queen",                 // this node coordinates the swarm
  "dataDir": "/var/lib/enbarr",    // state, identity, swarm key live here
  "web": {
    "enabled": true,               // serve the dashboard
    "addr": "127.0.0.1:7779"       // dashboard — loopback only
  },
  "gateway": {
    "addr": "0.0.0.0:7778",        // pawns enrol here — open inbound on the queen
    "deployKey": ""                // filled in for fleet provisioning
  },
  "network": {
    "port": 7777,                  // libp2p mesh
    "swarmKeyPath": "/var/lib/enbarr/swarm.key"
  },
  "agent": {
    "clearance": 200               // how much the agent may do when it acts
  },
  "platform": {
    "alertMode": "automatic"       // investigate on its own (vs "manual")
  }
}`;
const pawnConfigAnn = `{
  "role": "pawn",                  // this node reports to the queen
  "dataDir": "/var/lib/enbarr",
  "web": {
    "enabled": false,              // no dashboard on a pawn
    "addr": "127.0.0.1:7779"
  },
  "network": {
    "port": 7777,                  // libp2p mesh
    "swarmKeyPath": "/var/lib/enbarr/swarm.key",
    "joinCode": ""                 // paste the queen's invite token here
  },
  "agent": {
    "clearance": 200
  }
}`;
const inferenceConfig = `"inference": {
  "mode": "api",
  "endpoint": "http://localhost:8000",
  "model": "Qwen/Qwen2.5-7B-Instruct",
  "apiKey": ""
}`;
const winFwCmd = `New-NetFirewallRule -DisplayName "Enbarr Inbound" -Direction Inbound -LocalPort 7777,7778 -Protocol TCP -Action Allow`;
const rolloutExample = `.\\Deploy-EnbarrPawn.ps1 \`
  -QueenUrl "https://queen.corp.local:7779" \`
  -DeployKey "<64-hex>" \`
  -SwarmKeyPath "\\\\fileserver\\share\\swarm.key"`;

export default {
  name: "Docs",
  components: { Step, CodeBlock, Callout, OsTabs, Pills, FeatureGrid, Table },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const serverConfig = inject("serverConfig");
    const translation = translationStore();
    const t = translation.localize;

    const isRtl = computed(() => RTL_LANGS.indexOf(translation.VUE_APP_USER_LANGUAGE) !== -1);

    // nav stores translation KEYS; labels resolved with t() in the template
    const nav = [
      {
        title: "DOCS_GRP_GETSTARTED",
        items: [
          { id: "quickstart", label: "DOCS_NAV_QUICKSTART" },
          { id: "overview", label: "DOCS_NAV_OVERVIEW" },
          { id: "architecture", label: "DOCS_NAV_ARCHITECTURE" }
        ]
      },
      {
        title: "DOCS_GRP_SETUP",
        items: [
          { id: "install", label: "DOCS_NAV_INSTALL" },
          { id: "inference", label: "DOCS_NAV_INFERENCE" },
          { id: "enrol", label: "DOCS_NAV_ENROL" },
          { id: "rollout", label: "DOCS_NAV_ROLLOUT" }
        ]
      },
      {
        title: "DOCS_GRP_OPERATE",
        items: [
          { id: "licensing", label: "DOCS_NAV_LICENSING" },
          { id: "dashboard", label: "DOCS_NAV_DASHBOARD" }
        ]
      },
      {
        title: "DOCS_GRP_REFERENCE",
        items: [{ id: "reference", label: "DOCS_NAV_REFERENCE" }]
      }
    ];

    const SUBTITLES = {
      quickstart: "DOCS_QS_SUBTITLE",
      overview: "DOCS_OV_SUBTITLE",
      architecture: "DOCS_ARCH_SUBTITLE",
      install: "DOCS_INST_SUBTITLE",
      inference: "DOCS_INF_SUBTITLE",
      enrol: "DOCS_ENR_SUBTITLE",
      rollout: "DOCS_ROLL_SUBTITLE",
      licensing: "DOCS_LIC_SUBTITLE",
      dashboard: "DOCS_DASH_SUBTITLE",
      reference: "DOCS_REF_SUBTITLE"
    };

    const flat = nav.flatMap((g) => g.items);
    const validIds = new Set(flat.map((i) => i.id));

    const section = computed(() => {
      const s = route.params.section;
      return s && validIds.has(s) ? s : "quickstart";
    });
    const subtitleKey = computed(() => SUBTITLES[section.value] || "");

    const activeItem = computed(() => flat.find((i) => i.id === section.value) || flat[0]);
    const activeGroup = computed(() => {
      const g = nav.find((grp) => grp.items.some((i) => i.id === section.value));
      return g ? g.title : "";
    });
    const idx = computed(() => flat.findIndex((i) => i.id === section.value));
    const prevItem = computed(() => (idx.value > 0 ? flat[idx.value - 1] : null));
    const nextItem = computed(() => (idx.value < flat.length - 1 ? flat[idx.value + 1] : null));

    const os = ref(localStorage.getItem("enbarr-docs-os") || "linux");
    const setOs = (v) => {
      os.value = v;
      localStorage.setItem("enbarr-docs-os", v);
    };

    const configView = ref(localStorage.getItem("enbarr-docs-config") || "queen");
    const setConfigView = (v) => {
      configView.value = v;
      localStorage.setItem("enbarr-docs-config", v);
    };

    const goto = (id) => {
      router.push({ path: "/docs/" + id }).catch(() => {});
    };

    // delegated handler so localized HTML can carry <a data-goto="section"> links
    const onDocClick = (e) => {
      const a = e.target.closest("[data-goto]");
      if (a) {
        e.preventDefault();
        goto(a.getAttribute("data-goto"));
      }
    };

    const cp = computed(() => ({ copy: t("DOCS_COPY"), copied: t("DOCS_COPIED") }));

    return {
      nav, section, subtitleKey, activeItem, activeGroup, prevItem, nextItem,
      os, setOs, configView, setConfigView, goto, onDocClick, serverConfig, t, isRtl, cp,
      queenConfig, pawnConfig, queenConfigAnn, pawnConfigAnn, inferenceConfig, winFwCmd, rolloutExample
    };
  }
};
</script>

<style scoped>
/* The page inherits the global design tokens defined in App.vue (:root / [data-theme=dark]),
   so it follows enbarr.ai's colour scheme and theme switch automatically. */

.docs {
  position: relative;
  min-height: 100vh;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-family: var(--font-body);
}

/* ---- ambient decor ---- */
.docs-decor { position: fixed; inset: 0; pointer-events: none; z-index: 0; overflow: hidden; }
.decor-orb { position: absolute; border-radius: 50%; filter: blur(140px); opacity: 0.12; }
.orb-1 {
  width: 460px; height: 460px; top: -160px; right: -120px;
  background: radial-gradient(circle, rgba(124,58,237,0.30), rgba(99,102,241,0.10) 50%, transparent 70%);
}
.orb-2 {
  width: 380px; height: 380px; bottom: 6%; left: -120px;
  background: radial-gradient(circle, rgba(6,182,212,0.22), rgba(99,102,241,0.08) 50%, transparent 70%);
}
.orb-3 {
  width: 320px; height: 320px; top: 40%; right: 8%;
  background: radial-gradient(circle, rgba(236,72,153,0.16), transparent 70%);
}

/* ---- shell / layout ---- */
.docs-shell {
  position: relative;
  z-index: 1;
  display: flex;
  gap: 0;
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 24px;
  align-items: flex-start;
}

/* ---- sidebar ---- */
.docs-sidebar {
  width: 248px;
  flex: 0 0 248px;
  position: sticky;
  top: 96px;
  align-self: flex-start;
  max-height: calc(100vh - 116px);
  overflow-y: auto;
  padding: 72px 0 60px;
}
.docs-eyebrow {
  font-family: var(--font-mono);
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: var(--accent-purple);
  margin-bottom: 26px;
}
.docs-navgroup { margin-bottom: 26px; }
.docs-navgroup-title {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 10px;
}
.docs-navlink {
  display: block;
  padding: 7px 14px;
  margin: 2px 0;
  font-size: 0.92em;
  color: var(--text-secondary);
  border-left: 2px solid transparent;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease, background 0.15s ease;
}
.docs-navlink:hover { color: var(--text-primary); }
.docs-navlink.is-active {
  color: var(--accent-purple);
  border-left-color: var(--accent-purple);
  background: rgba(124, 58, 237, 0.06);
  font-weight: 600;
}

/* ---- mobile bar ---- */
.docs-mobilebar {
  position: relative;
  z-index: 2;
  padding: 24px 18px 0;
  max-width: 760px;
  margin: 0 auto;
}
.docs-mobilebar-select {
  width: 100%;
  padding: 12px 14px;
  font-family: var(--font-body);
  font-size: 0.95em;
  color: var(--text-primary);
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-left: 3px solid var(--accent-purple);
  border-radius: var(--radius-sm);
}

/* ---- content column ---- */
.docs-content {
  flex: 1 1 auto;
  min-width: 0;
  padding: 72px 0 80px 56px;
}
.docs-article { max-width: 760px; }

.docs-breadcrumb {
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: 1px;
  color: var(--text-muted);
  margin-bottom: 16px;
  text-transform: uppercase;
}
.docs-breadcrumb .sep { margin: 0 8px; color: var(--magenta); opacity: 0.7; }

.docs-h1 {
  font-family: var(--font-heading);
  font-size: 2.4em;
  font-weight: 800;
  line-height: 1.12;
  letter-spacing: -1px;
  margin: 0 0 12px;
  background: var(--gradient-text);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.docs-subtitle {
  font-size: 1.18em;
  line-height: 1.55;
  font-weight: 400;
  color: var(--text-muted);
  margin: 0 0 22px;
}
.docs-titlerule {
  width: 64px;
  height: 3px;
  border-radius: 2px;
  background: var(--gradient-cta);
  margin: 0 0 34px;
}
.docs.is-rtl .docs-titlerule { margin-left: auto; }
.docs-lead {
  font-size: 1.06em;
  line-height: 1.78;
  color: var(--text-secondary);
  margin: 0 0 30px;
}

/* deep selectors so generated/child-component markup is styled */
.docs-content :deep(.docs-h2) {
  font-family: var(--font-heading);
  font-size: 1.5em;
  font-weight: 700;
  letter-spacing: -0.4px;
  color: var(--text-primary);
  margin: 46px 0 14px;
  padding-top: 16px;
  border-top: 1px solid var(--border-subtle);
  -webkit-text-fill-color: currentColor;
  position: relative;
}
.docs-content :deep(.docs-h2)::before {
  content: "";
  position: absolute;
  top: -1px;
  left: 0;
  width: 48px;
  height: 2px;
  background: var(--gradient-divider);
}
.docs.is-rtl .docs-content :deep(.docs-h2)::before { left: auto; right: 0; }
.docs-content :deep(.docs-h3) {
  font-family: var(--font-heading);
  font-size: 1.08em;
  font-weight: 600;
  color: var(--text-primary);
  margin: 26px 0 10px;
}
.docs-content :deep(p) { line-height: 1.78; color: var(--text-secondary); margin: 0 0 16px; }
.docs-content :deep(strong) { color: var(--text-primary); font-weight: 600; }
.docs-content :deep(em) { color: var(--accent-cyan); font-style: normal; }
.docs-content :deep(a) { color: var(--accent-purple); cursor: pointer; }
.docs-content :deep(a:hover) { color: var(--purple-deep); text-decoration: underline; }

.docs-content :deep(.docs-list),
.docs-content :deep(.docs-steps-ol) {
  margin: 0 0 18px;
  padding-left: 22px;
  color: var(--text-secondary);
  line-height: 1.8;
}
.docs-content :deep(.docs-list li),
.docs-content :deep(.docs-steps-ol li) { margin-bottom: 8px; }
.docs-content :deep(.docs-steps-ol li)::marker { color: var(--accent-purple); font-family: var(--font-mono); font-weight: 700; }
.docs-content :deep(.docs-note) { font-size: 0.92em; color: var(--text-muted); }

/* inline code */
.docs-content :deep(code) {
  font-family: var(--font-mono);
  font-size: 0.86em;
  padding: 2px 7px;
  border-radius: 5px;
  background: var(--bg-input);
  color: var(--accent-purple);
  border: 1px solid var(--border-subtle);
}
.docs-content :deep(.docs-kbd) {
  font-family: var(--font-mono);
  font-size: 0.84em;
  padding: 3px 8px;
  border-radius: 5px;
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  color: var(--text-primary);
  white-space: nowrap;
}

/* ---- code block ---- */
.docs-content :deep(.docs-code) {
  position: relative;
  margin: 0 0 20px;
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-left: 3px solid var(--accent-purple);
  border-radius: var(--radius-sm);
  overflow: hidden;
}
.docs-content :deep(.docs-code pre) {
  margin: 0;
  padding: 18px 20px;
  overflow-x: auto;
}
.docs-content :deep(.docs-code code) {
  font-family: var(--font-mono);
  font-size: 0.86em;
  line-height: 1.7;
  color: var(--text-primary);
  background: none;
  border: none;
  padding: 0;
  white-space: pre;
}
.docs-content :deep(.docs-code-copy) {
  position: absolute;
  top: 10px;
  right: 10px;
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--text-muted);
  background: var(--bg-primary);
  border: 1px solid var(--border-subtle);
  border-radius: 4px;
  padding: 4px 9px;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
}
.docs-content :deep(.docs-code-copy:hover) { color: var(--accent-purple); border-color: var(--border-hover); }

/* ---- OS tabs / pills ---- */
.docs-content :deep(.docs-ostabs) { margin: 0 0 28px; }
.docs-content :deep(.docs-ostabs-track) {
  display: inline-flex;
  padding: 4px;
  gap: 4px;
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
}
.docs-content :deep(.docs-ostab) {
  font-family: var(--font-heading);
  font-size: 0.9em;
  font-weight: 600;
  color: var(--text-secondary);
  background: none;
  border: none;
  padding: 8px 22px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: all 0.18s ease;
}
.docs-content :deep(.docs-ostab:hover) { color: var(--text-primary); }
.docs-content :deep(.docs-ostab.is-active) {
  color: #fff;
  background: var(--gradient-primary);
  box-shadow: var(--shadow-sm);
}

/* ---- steps ---- */
.docs-content :deep(.docs-step) { display: flex; gap: 20px; }
.docs-content :deep(.docs-step-rail) {
  position: relative;
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.docs-content :deep(.docs-step-num) {
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-mono);
  font-size: 0.9em;
  font-weight: 700;
  color: #fff;
  background: var(--gradient-primary);
  border-radius: 50%;
  z-index: 1;
}
.docs-content :deep(.docs-step:not(.is-last) .docs-step-rail)::after {
  content: "";
  position: absolute;
  top: 34px;
  bottom: -6px;
  width: 2px;
  background: var(--border-subtle);
}
.docs-content :deep(.docs-step-body) { padding-bottom: 26px; flex: 1 1 auto; min-width: 0; }
.docs-content :deep(.docs-step-title) {
  font-family: var(--font-heading);
  font-size: 1.12em;
  font-weight: 700;
  color: var(--text-primary);
  margin: 5px 0 12px;
}

/* ---- callouts (colour-varied by type) ---- */
.docs-content :deep(.docs-callout) {
  margin: 24px 0;
  padding: 16px 20px;
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-left: 3px solid var(--accent-purple);
  border-radius: var(--radius-sm);
}
.docs-content :deep(.docs-callout.is-warn) { border-left-color: var(--rose); }
.docs-content :deep(.docs-callout.is-next) { border-left-color: var(--indigo); }
.docs-content :deep(.docs-callout.is-tip) { border-left-color: var(--accent-cyan); }
.docs-content :deep(.docs-callout-label) {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--accent-purple);
  margin-bottom: 8px;
}
.docs-content :deep(.docs-callout.is-warn .docs-callout-label) { color: var(--rose); }
.docs-content :deep(.docs-callout.is-next .docs-callout-label) { color: var(--indigo); }
.docs-content :deep(.docs-callout.is-tip .docs-callout-label) { color: var(--accent-cyan); }
.docs-content :deep(.docs-callout-body) { color: var(--text-secondary); line-height: 1.7; }
.docs-content :deep(.docs-callout-body p) { margin: 0; }
.docs.is-rtl .docs-content :deep(.docs-callout) { border-left: none; border-right: 3px solid var(--accent-purple); }

/* ---- feature grid (accent stripes cycle theme colours) ---- */
.docs-content :deep(.docs-featuregrid) {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px;
  margin: 8px 0 24px;
}
.docs-content :deep(.docs-feature) {
  padding: 18px 18px;
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-top: 3px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  transition: border-color 0.18s ease, transform 0.18s ease;
}
.docs-content :deep(.docs-featuregrid.is-accent .docs-feature:nth-child(4n+1)) { border-top-color: var(--accent-purple); }
.docs-content :deep(.docs-featuregrid.is-accent .docs-feature:nth-child(4n+2)) { border-top-color: var(--accent-cyan); }
.docs-content :deep(.docs-featuregrid.is-accent .docs-feature:nth-child(4n+3)) { border-top-color: var(--magenta); }
.docs-content :deep(.docs-featuregrid.is-accent .docs-feature:nth-child(4n+4)) { border-top-color: var(--indigo); }
.docs-content :deep(.docs-feature:hover) { transform: translateY(-2px); }
.docs-content :deep(.docs-feature-t) {
  font-family: var(--font-heading);
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 6px;
}
.docs-content :deep(.docs-feature-d) { font-size: 0.92em; color: var(--text-secondary); line-height: 1.65; }

/* ---- tables ---- */
.docs-content :deep(.docs-tablewrap) { margin: 8px 0 24px; overflow-x: auto; }
.docs-content :deep(.docs-table) {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.92em;
}
.docs-content :deep(.docs-table th) {
  text-align: left;
  font-family: var(--font-mono);
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--text-muted);
  padding: 10px 14px;
  border-bottom: 2px solid var(--border-subtle);
}
.docs-content :deep(.docs-table td) {
  padding: 11px 14px;
  color: var(--text-secondary);
  border-bottom: 1px solid var(--border-subtle);
  vertical-align: top;
  line-height: 1.6;
}
.docs-content :deep(.docs-table tr:hover td) { background: var(--bg-card); }
.docs-content :deep(.docs-table td:first-child) {
  color: var(--accent-purple);
  font-family: var(--font-mono);
  font-size: 0.92em;
  white-space: nowrap;
}

/* ---- pager ---- */
.docs-pager {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  max-width: 760px;
  margin-top: 48px;
  padding-top: 26px;
  border-top: 1px solid var(--border-subtle);
}
.docs-pager-link {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px 20px;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
  min-width: 180px;
}
.docs-pager-link.next { text-align: right; align-items: flex-end; }
.docs-pager-link:hover { border-color: var(--border-hover); background: var(--bg-card); }
.docs-pager-dir {
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--text-muted);
}
.docs-pager-label { font-family: var(--font-heading); font-weight: 600; color: var(--accent-purple); }

/* ---- RTL (Arabic) ---- */
.docs.is-rtl .docs-content { direction: rtl; }
.docs.is-rtl :deep(.docs-list),
.docs.is-rtl :deep(.docs-steps-ol) { padding-left: 0; padding-right: 22px; }
.docs.is-rtl :deep(.docs-code) { border-left: none; border-right: 3px solid var(--accent-purple); }
.docs.is-rtl :deep(.docs-navlink) { border-left: none; border-right: 2px solid transparent; }
.docs.is-rtl :deep(.docs-code code) { direction: ltr; text-align: left; display: block; }

/* ---- responsive ---- */
@media (max-width: 1024px) {
  .docs-content { padding-left: 36px; }
  .docs-sidebar { flex-basis: 210px; width: 210px; }
}
@media (max-width: 768px) {
  .docs-content { padding: 20px 0 60px; }
  .docs-shell { padding: 0 18px; }
  .docs-h1 { font-size: 1.9em; }
  .docs-subtitle { font-size: 1.05em; }
  .docs-article { max-width: 100%; }
  .docs-pager { flex-direction: column; }
  .docs-pager-link { min-width: 0; }
}
</style>
